import { useState } from "react";
import { ShieldCheck, Plus, Trash2, CheckCircle2, XCircle, Wrench, FileText } from "lucide-react";
import { useAzure } from "@/context/AzureContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

export default function AzurePolicy() {
  const { state, run, refresh } = useAzure();
  const defs = state?.policy_definitions || [];
  const assignments = state?.policy_assignments || [];
  const compliance = state?.policy_compliance || [];
  const rgs = state?.resource_groups || [];
  const subs = state?.subscriptions || [];
  const [tab, setTab] = useState("compliance");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ definition_id: "require-tag", scope_type: "resourceGroup", scope_name: "", tag_name: "" });

  const compFor = (id) => compliance.find((c) => c.assignment_id === id) || {};

  const assign = async () => {
    setBusy(true);
    const r = await run(() => api.post("/azure/policy/assignments", form), { success: "Policy assigned" });
    setBusy(false);
    if (r.ok) { setOpen(false); setForm({ definition_id: "require-tag", scope_type: "resourceGroup", scope_name: "", tag_name: "" }); refresh(); }
  };
  const remove = async (id) => { const r = await run(() => api.delete(`/azure/policy/assignments/${id}`), { success: "Assignment deleted" }); if (r.ok) refresh(); };
  const remediate = async (id) => { const r = await run(() => api.post(`/azure/policy/assignments/${id}/remediate`), { success: "Remediation task created" }); if (r.ok) refresh(); };

  return (
    <div data-testid="az-policy">
      <div className="flex items-center gap-2 border-b border-border pb-3 mb-3">
        <ShieldCheck className="h-5 w-5 text-[#0078d4]" /><h2 className="font-semibold">Policy</h2>
        <Button size="sm" className="ml-auto gap-1.5 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={() => setOpen(true)} data-testid="az-policy-assign-btn">
          <Plus className="h-4 w-4" /> Assign policy
        </Button>
      </div>

      <div className="flex gap-1 mb-3 text-sm">
        {[["compliance", "Compliance"], ["assignments", "Assignments"], ["definitions", "Definitions"]].map(([id, l]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`az-policy-tab-${id}`}
            className={`px-3 py-1.5 rounded-md ${tab === id ? "bg-[#0078d4] text-white" : "text-muted-foreground hover:bg-muted"}`}>{l}</button>
        ))}
      </div>

      {tab === "compliance" && (
        <div className="space-y-3" data-testid="az-policy-compliance">
          {assignments.length === 0 && <p className="text-sm text-muted-foreground">No policy assignments yet. Assign a policy to a resource group or subscription to start enforcing governance.</p>}
          {assignments.map((a) => {
            const c = compFor(a.id);
            const ok = c.compliant;
            return (
              <div key={a.id} className="rounded-lg border border-border bg-card p-4" data-testid={`az-policy-comp-${a.id}`}>
                <div className="flex items-center gap-2">
                  {ok ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <XCircle className="h-5 w-5 text-rose-500" />}
                  <span className="font-semibold text-sm">{a.display_name}</span>
                  <span className="text-[11px] px-2 py-0.5 rounded bg-muted">{a.effect}</span>
                  <span className="text-[11px] px-2 py-0.5 rounded bg-secondary">Tag: {a.parameters?.tagName}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{a.scope?.type === "subscription" ? `Sub: ${a.scope?.name}` : `RG: ${a.scope?.name}`}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1.5">
                  {c.total || 0} resource(s) in scope · <span className={ok ? "text-emerald-600" : "text-rose-600 font-medium"}>{c.noncompliant_count || 0} non-compliant</span>
                </div>
                {(c.noncompliant || []).length > 0 && (
                  <ul className="mt-2 text-xs space-y-0.5">
                    {c.noncompliant.map((r) => <li key={r.id} className="flex items-center gap-1.5 text-rose-600"><XCircle className="h-3 w-3" /> {r.name} <span className="text-muted-foreground">({r.type.split("/").slice(-1)[0]})</span></li>)}
                  </ul>
                )}
                {c.remediable && (c.noncompliant_count || 0) > 0 && (
                  <Button size="sm" variant="outline" className="mt-2 gap-1.5" onClick={() => remediate(a.id)} data-testid={`az-policy-remediate-${a.id}`}>
                    <Wrench className="h-3.5 w-3.5" /> Create remediation task
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === "assignments" && (
        <div className="border border-border rounded-md overflow-hidden" data-testid="az-policy-assignments">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Effect</TableHead><TableHead>Tag</TableHead><TableHead>Scope</TableHead><TableHead>Enforcement</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {assignments.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium">{a.display_name}</TableCell>
                  <TableCell>{a.effect}</TableCell>
                  <TableCell>{a.parameters?.tagName}</TableCell>
                  <TableCell className="text-xs">{a.scope?.type === "subscription" ? `Subscription: ${a.scope?.name}` : `RG: ${a.scope?.name}`}</TableCell>
                  <TableCell className="text-xs">{a.enforcement_mode || "Default"}</TableCell>
                  <TableCell><Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => remove(a.id)} data-testid={`az-policy-delete-${a.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button></TableCell>
                </TableRow>
              ))}
              {assignments.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6">No assignments.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}

      {tab === "definitions" && (
        <div className="space-y-2" data-testid="az-policy-definitions">
          {defs.map((d) => (
            <div key={d.id} className="rounded-lg border border-border bg-card p-3">
              <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-[#0078d4]" /><span className="font-medium text-sm">{d.display_name}</span>
                <span className="text-[11px] px-2 py-0.5 rounded bg-muted">{d.effect}</span>
                <span className="text-[11px] px-2 py-0.5 rounded bg-secondary">{d.category}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{d.description}</p>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="az-policy-dialog">
          <DialogHeader><DialogTitle>Assign policy</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Policy definition</Label>
              <Select value={form.definition_id} onValueChange={(v) => setForm((f) => ({ ...f, definition_id: v }))}>
                <SelectTrigger data-testid="az-policy-def"><SelectValue /></SelectTrigger>
                <SelectContent>{defs.map((d) => <SelectItem key={d.id} value={d.id}>{d.display_name} ({d.effect})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Scope type</Label>
                <Select value={form.scope_type} onValueChange={(v) => setForm((f) => ({ ...f, scope_type: v, scope_name: "" }))}>
                  <SelectTrigger data-testid="az-policy-scope-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resourceGroup">Resource group</SelectItem>
                    <SelectItem value="subscription">Subscription</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Scope</Label>
                <Select value={form.scope_name} onValueChange={(v) => setForm((f) => ({ ...f, scope_name: v }))}>
                  <SelectTrigger data-testid="az-policy-scope-name"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {(form.scope_type === "subscription" ? subs.map((s) => s.name) : rgs.map((g) => g.name)).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Tag name (parameter)</Label><Input value={form.tag_name} onChange={(e) => setForm((f) => ({ ...f, tag_name: e.target.value }))} placeholder="Cost Center" data-testid="az-policy-tag" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#0078d4] hover:bg-[#106ebe] text-white" disabled={busy || !form.scope_name || !form.tag_name} onClick={assign} data-testid="az-policy-submit">
              {busy ? "Assigning…" : "Assign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
